import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useBusinessAuth } from '../context/BusinessAuthContext';
import { orgsAPI } from '../services/bizApi';

export default function OrgSwitcherPage() {
  const { t } = useTheme();
  const { setActiveOrgId, signOutGlobal } = useBusinessAuth();
  const [orgs, setOrgs] = useState(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const cardStyle = { background: t.surface, border: `1px solid ${t.borderSubtle}`, borderRadius: '1rem' };

  const load = useCallback(async () => {
    try {
      const { data } = await orgsAPI.mine();
      setOrgs(data.organizations);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your organizations');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const choose = (orgId) => {
    setActiveOrgId(orgId);
    navigate('/workspace', { replace: true });
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await orgsAPI.create({ name: newName.trim() });
      toast.success(`${data.name} created`);
      choose(data.id);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not create organization');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: t.pageBg, color: t.textPrimary, fontFamily: "'Barlow', sans-serif" }} data-testid="org-switcher-page">
      <div className="max-w-lg mx-auto">
        <h1 className="text-3xl mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Choose your business</h1>

        {orgs === null && <p className="text-sm" style={{ color: t.textMutedNew }}>Loading…</p>}

        {orgs?.length > 0 && (
          <div className="space-y-2 mb-8">
            {orgs.map((o) => (
              <button
                key={o.id}
                onClick={() => choose(o.id)}
                className="w-full text-left p-4 rounded-xl flex items-center justify-between hover:opacity-90"
                style={cardStyle}
                data-testid={`org-switcher-item-${o.id}`}
              >
                <span className="font-semibold">{o.name}</span>
                <span className="text-xs uppercase tracking-wide" style={{ color: t.textFaint }}>{o.role}</span>
              </button>
            ))}
          </div>
        )}

        {orgs?.length === 0 && (
          <p className="text-sm mb-6" style={{ color: t.textMutedNew }}>You're not part of any business yet — create one below.</p>
        )}

        <form onSubmit={create} className="p-5 space-y-3" style={cardStyle}>
          <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: t.textFaint }}>Create a new business</p>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Business name"
            className="w-full h-11 rounded-xl px-4 text-sm"
            style={{ background: t.inputBgNew, border: `1px solid ${t.inputBorderNew}`, color: t.textPrimary }}
            data-testid="org-switcher-new-name-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-50"
            style={{ background: t.accent }}
            data-testid="org-switcher-create-button"
          >
            Create business
          </button>
        </form>

        <button
          onClick={signOutGlobal}
          className="mt-6 text-xs underline"
          style={{ color: t.textFaint }}
          data-testid="business-signout-button"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
